import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { BlogCard } from '@/components/blog/BlogCard';
import { BlogCardSkeleton } from '@/components/blog/BlogCardSkeleton';
import { getSiteUrl } from '@/lib/siteUrl';

export const Route = createFileRoute('/blog/')({
  component: BlogListPage,
});

const PAGE_TITLE = 'Blog — AITOS Labs LLC';
const PAGE_DESCRIPTION =
  'POD operations, AI in ecommerce, marketplace strategy, and notes from the AITOS brand portfolio (Elzary, Avyra Atlas).';

function BlogListPage() {
  const siteUrl = getSiteUrl();
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState<string | undefined>(undefined);

  const { data: categoriesData } = useQuery({
    queryKey: ['blog-categories'],
    queryFn: async () => {
      const res = await fetch('/api/blog/categories');
      if (!res.ok) return [];
      const json = await res.json();
      return json.data || [];
    },
  });
  const categoriesList = categoriesData ?? [];

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['blog-posts', page, category],
    queryFn: async () => {
      const url = new URL(`${window.location.origin}/api/blog/posts`);
      url.searchParams.set('page', page.toString());
      url.searchParams.set('limit', '9');
      if (category) url.searchParams.set('category', category);

      const res = await fetch(url.toString());
      if (!res.ok) throw new Error('Failed to fetch');
      const json = await res.json();
      return json.data;
    },
  });

  const isDataLoading = isLoading || isFetching;

  const posts = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  const blogListingSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'AITOS Blog',
    description: PAGE_DESCRIPTION,
    url: `${siteUrl}/blog`,
    publisher: {
      '@type': 'Organization',
      name: 'AITOS',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.webp`,
      },
    },
    blogPost: posts.map(
      (post: {
        slug: string;
        title: string;
        excerpt: string | null;
        featuredImageUrl: string | null;
        publishedAt: string | null;
        author: { name: string };
      }) => ({
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt ?? post.title,
        image: post.featuredImageUrl ?? `${siteUrl}/logo.webp`,
        url: `${siteUrl}/blog/${post.slug}`,
        datePublished: post.publishedAt ?? undefined,
        author: {
          '@type': 'Person',
          name: post.author?.name ?? 'AITOS',
        },
      })
    ),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
    ],
  };

  return (
    <div>
      <Helmet>
        {/* Primary */}
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          name="keywords"
          content="POD operations, Print-on-Demand operations, managed POD, Etsy operations, Amazon POD, AI in ecommerce, AITOS Labs LLC, Elzary, Avyra Atlas"
        />
        <link rel="canonical" href={`${siteUrl}/blog`} />
        {page > 1 && <link rel="prev" href={`${siteUrl}/blog?page=${page - 1}`} />}
        {page < totalPages && <link rel="next" href={`${siteUrl}/blog?page=${page + 1}`} />}

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="AITOS Blog" />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={`${siteUrl}/blog`} />
        <meta property="og:image" content={`${siteUrl}/logo.webp`} />
        <meta property="og:image:alt" content="AITOS Blog" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={`${siteUrl}/logo.webp`} />

        {/* Structured Data */}
        {posts.length > 0 && (
          <script type="application/ld+json">{JSON.stringify(blogListingSchema)}</script>
        )}
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Breadcrumb visible */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-gray-500 mb-6">
        <a href="/" className="hover:text-gray-300">
          Home
        </a>
        <span>/</span>
        <span className="text-gray-400">Blog</span>
      </nav>

      <h1 className="text-4xl font-bold mb-4">Blog</h1>
      <p className="text-gray-400 mb-8">
        POD operations, AI in ecommerce, marketplace strategy, and notes from the AITOS brand
        portfolio.
      </p>

      {/* Category Filter */}
      <div className="flex gap-4 mb-8 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent">
        <button
          type="button"
          onClick={() => {
            setCategory(undefined);
            setPage(1);
          }}
          className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${!category ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
        >
          All posts
        </button>
        {categoriesList.map((c: any) => (
          <button
            key={c.id}
            type="button"
            onClick={() => {
              setCategory(c.slug);
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${category === c.slug ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {isDataLoading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {Array.from({ length: 6 }, (_, i) => (
            <BlogCardSkeleton key={`skeleton-${i.toString()}`} />
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map(
            (post: {
              id: number;
              slug: string;
              title: string;
              excerpt: string | null;
              featuredImageUrl: string | null;
              featuredImageThumbUrl?: string | null;
              featuredImageAlt?: string | null;
              author: { name: string };
              publishedAt: string | null;
              tags?: string[];
            }) => (
              <BlogCard
                key={post.id}
                slug={post.slug}
                title={post.title}
                excerpt={post.excerpt}
                featuredImageUrl={post.featuredImageUrl}
                featuredImageThumbUrl={post.featuredImageThumbUrl}
                author={post.author}
                publishedAt={post.publishedAt}
                tags={post.tags}
              />
            )
          )}
        </div>
      )}

      {!isDataLoading && posts.length === 0 && (
        <div className="text-center py-24 text-gray-500">
          No posts published yet. Check back soon.
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-12">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-4 py-2 rounded-lg border border-gray-700 text-gray-400 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          <span className="flex items-center px-4 text-gray-400">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-4 py-2 rounded-lg border border-gray-700 text-gray-400 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
